// client/src/lib/support/inboxFilterSession.ts
import type { InboxFilters } from '../../types/support/inbox';
import { DEFAULT_INBOX_FILTERS } from '../../types/support/inbox';
import { CASE_TYPE_CONFIG } from '../../types/supportCase';

/**
 * ISS-517 — the inbox's working filters, kept per browser tab (sessionStorage)
 * and per user so they survive leaving the inbox (e.g. "View Lead") and coming
 * back. This is deliberately NOT the saved default: that lives on the user's
 * preferences doc and is written through the support_dispatch callable.
 *
 * Every storage access is wrapped: sessionStorage throws in private mode / when
 * blocked / when full, and a filter-memory convenience must never break the page.
 */
const KEY_PREFIX = 'supportInbox.filters.v1:';

const keyFor = (userId: string) => `${KEY_PREFIX}${userId}`;

const CHANNELS: readonly string[] = ['all', 'calls', 'web_leads', 'cases', 'mentions'];
const STATUSES: readonly string[] = ['all', 'open', 'closed'];
// Same set the server's sanitizeInboxDefaults accepts: every CaseType (keys of
// CASE_TYPE_CONFIG, the one client source of truth) plus the 'all' sentinel.
const CASE_TYPES: readonly string[] = ['all', ...Object.keys(CASE_TYPE_CONFIG)];
/** Matches the server's MAX_STRING_LEN for search / branch / assignee. */
const MAX_SEARCH_LEN = 200;

/**
 * Mirrors the server's isSafeFilterToken (preferencesService.ts): branch and
 * assignee are stored only when they are [a-zA-Z0-9 _-] and at most 200 chars.
 */
export function isSafeFilterToken(value: string): boolean {
  return value.length <= MAX_SEARCH_LEN && /^[a-zA-Z0-9 _-]*$/.test(value);
}

/** Coerce untrusted JSON into InboxFilters; unknown/invalid keys fall back to defaults. */
export function sanitizeInboxFilters(raw: unknown): InboxFilters | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const out: InboxFilters = { ...DEFAULT_INBOX_FILTERS };
  if (typeof r.channel === 'string' && CHANNELS.includes(r.channel)) {
    out.channel = r.channel as InboxFilters['channel'];
  }
  if (typeof r.status === 'string' && STATUSES.includes(r.status)) {
    out.status = r.status as InboxFilters['status'];
  }
  if (typeof r.branch === 'string' && isSafeFilterToken(r.branch)) out.branch = r.branch;
  if (typeof r.assignee === 'string' && isSafeFilterToken(r.assignee)) out.assignee = r.assignee;
  if (typeof r.search === 'string') out.search = r.search.slice(0, MAX_SEARCH_LEN);
  if (typeof r.type === 'string' && CASE_TYPES.includes(r.type)) {
    out.type = r.type as InboxFilters['type'];
  }
  if (typeof r.needsReviewOnly === 'boolean') out.needsReviewOnly = r.needsReviewOnly;
  return out;
}

export function loadSessionFilters(userId: string): InboxFilters | null {
  if (!userId) return null;
  try {
    const text = window.sessionStorage.getItem(keyFor(userId));
    if (!text) return null;
    return sanitizeInboxFilters(JSON.parse(text));
  } catch {
    return null;
  }
}

export function saveSessionFilters(userId: string, filters: InboxFilters): void {
  if (!userId) return;
  try {
    window.sessionStorage.setItem(keyFor(userId), JSON.stringify(filters));
  } catch {
    // Storage unavailable: filters simply don't persist across navigation.
  }
}

/** The shape written as "my default": working filters minus the transient search text. */
export function toSavedDefault(filters: InboxFilters): InboxFilters {
  return {
    ...filters,
    search: '',
    type: filters.type ?? 'all',
    needsReviewOnly: filters.needsReviewOnly ?? false,
  };
}

/** True when two filter sets mean the same thing (absent optional keys = cleared). */
export function filtersMatch(a: InboxFilters, b: InboxFilters): boolean {
  const norm = (f: InboxFilters) => ({
    ...f,
    type: f.type ?? 'all',
    needsReviewOnly: f.needsReviewOnly ?? false,
  });
  const x = norm(a);
  const y = norm(b);
  return (Object.keys(x) as (keyof InboxFilters)[]).every((k) => x[k] === y[k]);
}

/** Remove every remembered inbox filter (all users) from this tab — called on logout. */
export function clearSessionFilters(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key && key.startsWith(KEY_PREFIX)) doomed.push(key);
    }
    doomed.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // Storage unavailable: nothing was remembered, nothing to clear.
  }
}

/**
 * True when the server stored every key we sent. The server sanitizer drops
 * invalid keys silently, so a "success" response can still be a partial save.
 */
export function savedDefaultMatches(
  sent: InboxFilters,
  stored: Partial<InboxFilters> | undefined,
): boolean {
  if (!stored) return false;
  return (Object.keys(sent) as (keyof InboxFilters)[]).every((k) => stored[k] === sent[k]);
}
